// Redux slice for managing soft tags, filters, and pagination
import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type {
  Tags,
  SoftTagsResponse,
  TagsFilterOptions,
  TagsSearchFilters,
  PaginationParams,
  UploadBulkImportSuccessPayload,
  UploadBulkImportRejectPayload,
} from '../types/tags.types';
import {
  getSearchTags,
  getSoftTag,
  getSoftTags,
  uploadBulkImportFile,
} from './tags.thunk';
import {
  createSearchTagsResponseMapping,
  softTagsResponseMapping,
} from '../utils/tags.helpers';
import type { SoftTagState } from '@/features/createTags/utils/types';

// State shape for tags feature
interface TagsState {
  tags: Tags[]; // List of soft tags
  loading: boolean; // Loading state
  error: string | null; // Error message
  pagination: PaginationParams; // Pagination info
  filtersOptions: TagsFilterOptions; // Dropdown filter options
  filtersSearch: TagsSearchFilters; // Current search filter values
  searchTags: [];
  searchTagsLoading: boolean;
  getSoftTagLoading: boolean;
  getSoftTagError: string | null;
  softTag: SoftTagState | null;
  uploadBulkImportLoading: boolean;
  uploadBulkImportError: string | null;
  uploadBulkImportErrorCsv: string | null;
}

// Initial state for tags slice
const initialState: TagsState = {
  tags: [],
  loading: false,
  error: null,
  pagination: {
    count: 0,
    size: 10,
    page: 1,
    sort_by: '',
    sort_order: 'asc',
  },
  filtersOptions: {
    productionSites: [],
    areas: [],
    productionUnits: [],
  },
  filtersSearch: {
    search_term: '',
    site: '',
    area: '',
    production_unit: '',
  },
  searchTags: [],
  searchTagsLoading: false,
  getSoftTagLoading: false,
  getSoftTagError: null,
  softTag: null,
  uploadBulkImportLoading: false,
  uploadBulkImportError: null,
  uploadBulkImportErrorCsv: null,
};

// Slice for tags feature with reducers and extraReducers for async actions
const tagsSlice = createSlice({
  name: 'tags',
  initialState,
  reducers: {
    // Update pagination state
    setPagination: (
      state,
      action: PayloadAction<{ page?: number; size?: number }>
    ) => {
      if (action.payload.page !== undefined) {
        state.pagination.page = action.payload.page;
      }
      if (action.payload.size !== undefined) {
        state.pagination.size = action.payload.size;
      }
    },

    // Update sorting state
    setSorting: (
      state,
      action: PayloadAction<{ sort_by: string; sort_order: 'asc' | 'desc' }>
    ) => {
      state.pagination.sort_by = action.payload.sort_by;
      state.pagination.sort_order = action.payload.sort_order;
    },

    // Reset filters and pagination to initial state
    resetFilters: state => {
      state.pagination = initialState.pagination;
    },

    // Clear all tags and reset count
    clearTags: state => {
      state.tags = [];
      state.pagination.count = 0;
    },

    // Store requested search filters
    setSearchFilter: (state, action: PayloadAction<TagsSearchFilters>) => {
      state.filtersSearch = action.payload;
    },

    // Clear requested search filters and searchTags value
    clearSearchFilters: state => {
      state.filtersSearch = {
        search_term: '',
        site: '',
        area: '',
        production_unit: '',
      };
      state.searchTags = [];
    },
    clearSoftTag: state => {
      state.getSoftTagLoading = false;
      state.getSoftTagError = null;
      state.softTag = null;
    },
  },
  extraReducers: builder => {
    builder
      // Handle getSoftTags pending state
      .addCase(getSoftTags.pending, state => {
        state.loading = true;
        state.error = null;
      })

      // Handle getSoftTags fulfilled state
      .addCase(
        getSoftTags.fulfilled,
        (state, action: PayloadAction<SoftTagsResponse>) => {
          state.loading = false;
          state.error = null;
          state.tags = action.payload.results;
          softTagsResponseMapping(state, action.payload);
        }
      )

      // Handle getSoftTags rejected state
      .addCase(getSoftTags.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) || 'Failed to fetch tags';
        state.tags = [];
      })
      .addCase(getSoftTag.pending, state => {
        state.getSoftTagLoading = true;
        state.getSoftTagError = null;
      })
      .addCase(getSoftTag.fulfilled, (state, action) => {
        state.getSoftTagLoading = false;
        state.getSoftTagError = null;
        state.softTag = action.payload;
      })
      .addCase(getSoftTag.rejected, (state, action) => {
        state.getSoftTagLoading = false;
        state.getSoftTagError =
          (action.payload as string) || 'Failed to fetch tag';
        state.softTag = null;
      })

      // Handle getSearchTags pending state
      .addCase(getSearchTags.pending, state => {
        state.searchTagsLoading = true;
        state.error = null;
      })

      // Handle getSearchTags fulfilled state
      .addCase(getSearchTags.fulfilled, (state, action) => {
        state.searchTagsLoading = false;
        state.error = null;
        createSearchTagsResponseMapping(state, action.payload);
      })

      // Handle getSearchTags rejected state
      .addCase(getSearchTags.rejected, (state, action) => {
        state.searchTagsLoading = false;
        state.error = (action.payload as string) || 'Failed to fetch tags';
        state.searchTags = [];
      })

      // Handle uploadBulkImportFile pending/fulfilled/rejected states
      .addCase(uploadBulkImportFile.pending, state => {
        state.uploadBulkImportLoading = true;
        state.uploadBulkImportError = null;
        state.uploadBulkImportErrorCsv = null;
      })
      .addCase(uploadBulkImportFile.fulfilled, (state, action) => {
        const payload = action.payload as UploadBulkImportSuccessPayload;

        state.uploadBulkImportLoading = false;
        state.uploadBulkImportError = payload.hasValidationErrors
          ? 'Bulk import completed with validation errors'
          : null;
        state.uploadBulkImportErrorCsv = payload.responseCsv;
      })
      .addCase(uploadBulkImportFile.rejected, (state, action) => {
        const payload = action.payload as
          | UploadBulkImportRejectPayload
          | undefined;

        state.uploadBulkImportLoading = false;
        state.uploadBulkImportError =
          payload?.message || 'Failed to upload bulk import file';
        state.uploadBulkImportErrorCsv = payload?.errorCsv ?? null;
      });
  },
});

// Export actions for use in components
export const {
  setPagination,
  setSorting,
  resetFilters,
  clearTags,
  setSearchFilter,
  clearSearchFilters,
  clearSoftTag,
} = tagsSlice.actions;

// Export reducer for store
export default tagsSlice.reducer;
